import React, { useEffect, useState, useRef } from 'react';
import type { ErrorInfo } from 'react-dom/client';
import type { ReactHydrationStats } from '@atlaskit/react-ufo/config';
import { createRouterSelector } from '@atlassian/react-resource-router';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { callWhenIdle } from '@atlassian/jira-call-when-idle';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { RenderAfterHydrationCompletion } from '@atlassian/jira-ssr-toolkit/src/RenderAfterHydrationCompletion.tsx';
import { fg } from '@atlassian/jira-feature-gating';

const HYDRATION_STATS: {
	enabled: boolean;
	hasFatalError: boolean;
	warningCount: number;
	minErrorComponentDepth: number | null;
	maxErrorComponentDepth: number | null;
} = {
	enabled: false,
	hasFatalError: false,
	warningCount: 0,
	minErrorComponentDepth: null,
	maxErrorComponentDepth: null,
};

const useRouteName = createRouterSelector((state) => state.route.name);

// Wait until React hydration is done. This is a bit crude, but we chain together a few idle callbacks with some space
// in case React pauses work for a bit, in which case we would incorrectly capture metrics before hydration is complete.
// Ideally would use <RenderAfterHydrationCompletion /> alone, but want to be absolutely sure this doesn't fire too
// early and report false success.
const delayUntilHydrationCompleted = (callback: () => void) => {
	callWhenIdle(() => {
		setTimeout(() => {
			callWhenIdle(() => {
				setTimeout(() => {
					callWhenIdle(callback);
				}, 2_500);
			});
		}, 2_500);
	});
};

const ReportOnce = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const initialRouteName = useRouteName();
	// Using state here because we want to capture the route name on initial render, and not have it change when the effect runs
	const [routeName] = useState(initialRouteName);
	const reported = useRef(false);

	useEffect(() => {
		if (!HYDRATION_STATS.enabled || reported.current) {
			return;
		}
		reported.current = true;

		let cancelled = false;
		delayUntilHydrationCompleted(() => {
			if (cancelled) {
				return;
			}
			const { hasFatalError, warningCount, minErrorComponentDepth, maxErrorComponentDepth } =
				HYDRATION_STATS;
			const successful = !hasFatalError;
			fireOperationalAnalytics(
				createAnalyticsEvent({}),
				successful ? 'reactHydrate success' : 'reactHydrate failure',
				{
					warningCount: bucketedWarningCount(warningCount),
					routeName,
				},
			);
			// When cleaning up, replace with @atlassian/jira-coinflip depending on the desired rate, or remove
			// completely if no longer needed and the analytics event above can be used instead
			if (fg('jfp-magma-hydration-report-to-splunk')) {
				log.safeInfoWithoutCustomerData(
					'react-hydration-reporting',
					successful ? 'Hydration success' : 'Hydration failure',
					{
						warningCount,
						routeName,
						minErrorComponentDepth,
						maxErrorComponentDepth,
					},
				);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [createAnalyticsEvent, routeName]);

	return null;
};

/**
 * Reports the overall success / failure of React hydration once complete. This is inside a React component so that it
 * can access analytics context and the current route.
 */
export const HydrationStatsReporter = () => (
	<RenderAfterHydrationCompletion>
		<ReportOnce />
	</RenderAfterHydrationCompletion>
);

export const getReactHydrationStats = (): ReactHydrationStats | undefined => {
	if (!HYDRATION_STATS.enabled) {
		return undefined;
	}
	return {
		successful: !HYDRATION_STATS.hasFatalError,
		warningCount: HYDRATION_STATS.warningCount,
		minWarningComponentDepth: HYDRATION_STATS.minErrorComponentDepth ?? undefined,
		maxWarningComponentDepth: HYDRATION_STATS.maxErrorComponentDepth ?? undefined,
	};
};

export const bucketedWarningCount = (warningCount: number): string => {
	if (warningCount <= 9) {
		return warningCount.toString();
	}
	return '10+';
};

/**
 * Enables high-level hydration stats based on hydration error logs.
 */
export const enableHydrationStatReporting = () => {
	HYDRATION_STATS.enabled = true;
};

/**
 * To be called for each error from React hydrateRoot#onRecoverableError which may be a hydration error.
 */
export const addErrorForHydrationStats = (error: Error, errorInfo: ErrorInfo) => {
	switch (classifyAsHydrationError(error)) {
		case HYDRATION_FATAL_ERROR:
			HYDRATION_STATS.hasFatalError = true;
			recordComponentDepthStats(errorInfo);
			break;
		case HYDRATION_WARNING:
			HYDRATION_STATS.warningCount++;
			recordComponentDepthStats(errorInfo);
			break;
		default:
			break;
	}
};

const HYDRATION_FATAL_ERROR = 'fatal';
const HYDRATION_WARNING = 'warning';
type HydrationErrorType = typeof HYDRATION_FATAL_ERROR | typeof HYDRATION_WARNING;
/**
 * Determines if the error is a hydration error and if so, what type.
 * This is fragile because it depends on the exact React version's error messages (18.2.0), defined here:
 * https://github.com/facebook/react/blob/v18.2.0/scripts/error-codes/codes.json
 * To do this properly requires patching React (again). Using best-effort detection like this is the less-bad option.
 */
export const classifyAsHydrationError = ({ message }: Error): HydrationErrorType | null => {
	if (process.env.NODE_ENV === 'production') {
		if (PROD_FATAL_MESSAGES.some((fatal) => message.includes(fatal))) {
			return HYDRATION_FATAL_ERROR;
		}
		if (PROD_WARNING_MESSAGES.some((warning) => message.includes(warning))) {
			return HYDRATION_WARNING;
		}
	} else {
		if (DEV_FATAL_MESSAGES.some((fatal) => message.includes(fatal))) {
			return HYDRATION_FATAL_ERROR;
		}
		if (DEV_WARNING_MESSAGES.some((warning) => message.includes(warning))) {
			return HYDRATION_WARNING;
		}
	}

	return null;
};

const PROD_FATAL_MESSAGES = [407, 419, 421, 422, 423, 424].map(
	(code) => `Minified React error #${code}`,
);
const PROD_WARNING_MESSAGES = [418, 425].map((code) => `Minified React error #${code}`);
const DEV_FATAL_MESSAGES = ['to client rendering'];
const DEV_WARNING_MESSAGES = [
	'Hydration failed because the initial UI does not match what was rendered on the server',
	'Text content does not match server-rendered HTML',
];

const recordComponentDepthStats = ({ componentStack }: ErrorInfo) => {
	if (!componentStack) {
		return;
	}
	// Number of components = number of lines after the error message
	const componentStackDepth = componentStack.match(/\n/g)?.length ?? 1;
	HYDRATION_STATS.minErrorComponentDepth = Math.min(
		componentStackDepth,
		HYDRATION_STATS.minErrorComponentDepth ?? Number.MAX_SAFE_INTEGER,
	);
	HYDRATION_STATS.maxErrorComponentDepth = Math.max(
		componentStackDepth,
		HYDRATION_STATS.maxErrorComponentDepth ?? 0,
	);
};
